import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { isValidId } from "@hooks/use-get/utils";
import type { AssetAssignment } from "@hooks/use-get/asset-assignments/types";

const ENDPOINT = "wp-json/smassets/v1/asset-assignments";

export type CreateAssetAssignmentInput = {
  asset_id: number;
  wp_user_id: number;
  blog_id?: number;
  notes?: string;
};

/**
 * POST /asset-assignments
 * Assigns an asset to a user. The backend closes any open record for the asset
 * and opens a new one.
 */
export const useCreateAssetAssignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl, blogId } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const createAssignment = useCallback(
    async (input: CreateAssetAssignmentInput): Promise<AssetAssignment | null> => {
      if (!isValidId(input.asset_id) || !isValidId(input.wp_user_id)) {
        setError("Invalid asset_id or wp_user_id");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const body: CreateAssetAssignmentInput = {
          ...input,
          blog_id: input.blog_id ?? (blogId !== null ? blogId : undefined),
        };

        const response = await fetchWithTokenRefresh(
          getApiUrl(ENDPOINT),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.authToken ?? ""}`,
            },
            body: JSON.stringify(body),
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(`Failed to create asset assignment: ${response.status}`);
        }

        return await response.json() as AssetAssignment;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error creating asset assignment";
        setError(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getApiUrl, blogId, user?.authToken, user?.refreshToken, refreshUserToken]
  );

  return { createAssignment, loading, error };
};
